import { MarkDownTokenParser } from "./markdown_token_parser.js";
import {
  darkenNavBar,
  loadNavBar,
  loadFooter,
  footerElements,
  setupNavBarLinks,
  loadDefaultLanguage,
  setLanguage,
} from "./static.js";

import {
  parseIcal,
  icalWorkerAddress,
  filterEventsByTerm,
} from "./ical-parser.js";

import { eventFilterTerms } from "./config.js";

// Import internationalization
import headerFi from "bundle-text:../public/content-md/member/header-fi.md";
import headerSv from "bundle-text:../public/content-md/member/header-sv.md";
import headerEn from "bundle-text:../public/content-md/member/header-en.md";
import kataFi from "bundle-text:../public/content-md/member/kata-fi.md";
import kataSv from "bundle-text:../public/content-md/member/kata-sv.md";
import kataEn from "bundle-text:../public/content-md/member/kata-en.md";
import shopsFi from "bundle-text:../public/content-md/member/shops-fi.md";
import shopsSv from "bundle-text:../public/content-md/member/shops-sv.md";
import shopsEn from "bundle-text:../public/content-md/member/shops-en.md";
import dojorouteFi from "bundle-text:../public/content-md/member/dojoroute-fi.md";
import dojorouteSv from "bundle-text:../public/content-md/member/dojoroute-sv.md";
import dojorouteEn from "bundle-text:../public/content-md/member/dojoroute-en.md";
import suomisportmodalFi from "bundle-text:../public/content-md/member/suomisportmodal-fi.md";
import suomisportmodalSv from "bundle-text:../public/content-md/member/suomisportmodal-sv.md";
import suomisportmodalEn from "bundle-text:../public/content-md/member/suomisportmodal-en.md";
import fkacalendarmodalFi from "bundle-text:../public/content-md/member/fkacalendarmodal-fi.md";
import fkacalendarmodalSv from "bundle-text:../public/content-md/member/fkacalendarmodal-sv.md";
import fkacalendarmodalEn from "bundle-text:../public/content-md/member/fkacalendarmodal-en.md";
import navbarFi from "bundle-text:../public/content-md/navbar/navbar-fi.md";
import navbarSv from "bundle-text:../public/content-md/navbar/navbar-sv.md";
import navbarEn from "bundle-text:../public/content-md/navbar/navbar-en.md";
import footerFi from "bundle-text:../public/content-md/footer/footer-fi.md";
import footerSv from "bundle-text:../public/content-md/footer/footer-sv.md";
import footerEn from "bundle-text:../public/content-md/footer/footer-en.md";

const markdownContent = {
  fi: {
    navbar: navbarFi,
    header: headerFi,
    kata: kataFi,
    shops: shopsFi,
    dojoroute: dojorouteFi,
    suomisportmodal: suomisportmodalFi,
    fkacalendarmodal: fkacalendarmodalFi,
    footer: footerFi,
  },
  sv: {
    navbar: navbarSv,
    header: headerSv,
    kata: kataSv,
    shops: shopsSv,
    dojoroute: dojorouteSv,
    suomisportmodal: suomisportmodalSv,
    fkacalendarmodal: fkacalendarmodalSv,
    footer: footerSv,
  },
  en: {
    navbar: navbarEn,
    header: headerEn,
    kata: kataEn,
    shops: shopsEn,
    dojoroute: dojorouteEn,
    suomisportmodal: suomisportmodalEn,
    fkacalendarmodal: fkacalendarmodalEn,
    footer: footerEn,
  },
};

async function renderContent(lang = "fi") {
  const navbarContent = new MarkDownTokenParser(markdownContent[lang].navbar);
  const headerContent = new MarkDownTokenParser(markdownContent[lang].header);
  const kataContent = new MarkDownTokenParser(markdownContent[lang].kata);
  const shopsContent = new MarkDownTokenParser(markdownContent[lang].shops);
  const dojorouteContent = new MarkDownTokenParser(
    markdownContent[lang].dojoroute
  );
  const suomisportmodalContent = new MarkDownTokenParser(
    markdownContent[lang].suomisportmodal
  );
  const fkacalendarmodalContent = new MarkDownTokenParser(
    markdownContent[lang].fkacalendarmodal
  );
  const footerContent = new MarkDownTokenParser(markdownContent[lang].footer);

  await navbarContent.render();
  await headerContent.render();
  await kataContent.render();
  await shopsContent.render();
  await dojorouteContent.render();
  await suomisportmodalContent.render();
  await fkacalendarmodalContent.render();
  await footerContent.render();
}

class FKAiCalEvents {
  constructor(events) {
    this.icalWorkerAddress = icalWorkerAddress;
    this.fkaElement = document.querySelector("#fkaEventBody");
  }
  async fetchEvents() {
    this.events = await parseIcal(this.icalWorkerAddress).then((result) =>
      filterEventsByTerm(result, eventFilterTerms)
    );
  }

  createEventString(event, lang) {
    if (event.oneDay) {
      return `<li>${event.startWeekDay[lang]} ${event.startDate}: <b>${event.summary}</b></li>`;
    } else {
      return `<li>${event.startWeekDay[lang]} ${event.startDate} - ${event.endWeekDay[lang]} ${event.endDate}: <b>${event.summary}</b></li>`;
    }
  }

  async renderEvents(lang = "fi") {
    await this.fetchEvents();
    const eventItems = this.events
      .map((e) => this.createEventString(e, lang))
      .join("");
    this.fkaElement.innerHTML = `<ul class="list-unstyled">${eventItems}</ul>`;
  }
}

async function renderMap() {
  // Map
  const map = L.map("eklofskaMap");
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap contributors",
  }).addTo(map);

  // Waypoints (lat, lng) — in the order you gave, finishing at the school
  const route = [
    [60.3434621395288, 25.585581689530198],
    [60.341293956438605, 25.58355766578249],
    [60.340449287346175, 25.58265580975097],
    [60.3386933068382, 25.581372328320732],
    [60.33887389075348, 25.58315913580204],
    [60.34009436273611, 25.58711024530295],
    [60.34039947359873, 25.58704732954657],
    [60.34056136799987, 25.586128759503364],
    [60.342754103134816, 25.586788057162313],
    [60.34292618213656, 25.587453353397347],
    // Destination: Eklöfska skolan
    [60.34312738926097, 25.587487660427865],
  ];

  // Draw the route
  const line = L.polyline(route, {
    color: "#113a78ff",
    weight: 6,
    opacity: 0.9,
    lineJoin: "round",
  }).addTo(map);

  // Start & end markers
  const start = route[0];
  const end = route[route.length - 1];

  // L.marker(start).addTo(map).bindPopup("Reitin alku");
  L.marker(end).addTo(map).bindPopup("Eklöfska skolan");

  // Fit view
  map.fitBounds(line.getBounds(), { padding: [24, 24] });
}

document.addEventListener("DOMContentLoaded", () => {
  loadNavBar()
    .then(() => darkenNavBar())
    .then(() => loadFooter())
    .then(() => setupNavBarLinks())
    .then(() => renderMap())
    .then(() => loadDefaultLanguage())
    .then((lang) => renderContent(lang))
    .then(() => {
      const languageButtons = document.querySelector("#navbar-changeLanguage");
      const languageFlags = document.querySelectorAll(".navbar-language-flag");
      // console.log(languageButtons);
      languageButtons.addEventListener("click", (e) => {
        e.preventDefault();
        // console.log("Ping");
        if (e.target.id === "changeFinnish") {
          renderContent("fi");
          setLanguage("fi");
          languageFlags.forEach((flag) => {
            flag.classList.remove("active");
          });
          e.target.classList.add("active");
        } else if (e.target.id === "changeSwedish") {
          renderContent("sv");
          setLanguage("sv");
          languageFlags.forEach((flag) => {
            flag.classList.remove("active");
          });
          e.target.classList.add("active");
        } else if (e.target.id === "changeEnglish") {
          renderContent("en");
          setLanguage("en");
          languageFlags.forEach((flag) => {
            flag.classList.remove("active");
          });
          e.target.classList.add("active");
        }
      });
    })
    .then(() => {
      const fka = new FKAiCalEvents();
      fka.fetchEvents().then(() => fka.renderEvents("fi"));
    });
});
